import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ToastKind, ToastService } from '../../core/toast.service';

/**
 * Pilha de avisos efêmeros (toasts), ancorada no canto inferior direito. Lê o
 * estado do {@link ToastService}; cada aviso pode ser fechado manualmente. A
 * região é `aria-live="polite"` para anunciar a mensagem em leitores de tela.
 */
@Component({
  selector: 'ub-toast',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="toast-host" aria-live="polite" aria-atomic="false">
      @for (toast of toasts(); track toast.id) {
        <div class="toast" [attr.data-kind]="toast.kind" role="status">
          <span class="toast__icon" aria-hidden="true">{{ icon(toast.kind) }}</span>
          <span class="toast__msg">{{ toast.message }}</span>
          <button type="button" class="toast__close" (click)="dismiss(toast.id)" aria-label="Fechar aviso">✕</button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .toast-host {
        position: fixed;
        right: 18px;
        bottom: 18px;
        z-index: 120;
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: min(360px, calc(100vw - 32px));
        pointer-events: none;
      }

      .toast {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 14px;
        border-radius: 11px;
        background: var(--s1);
        border: 1px solid var(--line2);
        box-shadow: 0 12px 34px rgba(0, 0, 0, 0.18);
        pointer-events: auto;
        animation: toast-rise 0.18s ease;
      }

      .toast__icon {
        width: 22px;
        height: 22px;
        border-radius: 6px;
        display: grid;
        place-items: center;
        font: 700 12px 'Inter';
        flex-shrink: 0;
        color: #fff;
        background: var(--pos);
      }

      .toast[data-kind='error'] .toast__icon {
        background: var(--neg);
      }

      .toast[data-kind='info'] .toast__icon {
        background: var(--info);
      }

      .toast__msg {
        flex: 1;
        font: 500 13px/1.45 'Inter';
        color: var(--text);
      }

      .toast__close {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        border: none;
        background: transparent;
        color: var(--muted);
        cursor: pointer;
        font-size: 11px;
      }

      .toast__close:hover {
        background: var(--s2);
        color: var(--text);
      }

      @keyframes toast-rise {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
      }
    `,
  ],
})
export class Toast {
  private readonly service = inject(ToastService);
  protected readonly toasts = this.service.toasts;

  protected dismiss(id: number): void {
    this.service.dismiss(id);
  }

  protected icon(kind: ToastKind): string {
    if (kind === 'error') {
      return '!';
    }
    if (kind === 'info') {
      return 'i';
    }
    return '✓';
  }
}
